import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setActivePointTab,
  setActiveTab,
} from "../../../redux/slice/transactionSlice";
import { setHeading } from "../../../redux/slice/headerSlice";
import { customerView } from "../../auth/auth";
import Loader from "../../partial/Loader";
import { getDate } from "../../../utils/helper";
import Header from "../../layout/Header";
import { useNavigate } from "react-router-dom";
import { formatApiDate, parseApiDateToTimestamp } from "../../../utils/parseApiDateToTimestamp";

export const formatPoints = (points) => {
  const formatNumber = (num) => {
    return Number.isInteger(num) ? num : num.toFixed(2);
  };

  if (points >= 10000000) {
    return `${formatNumber(points / 10000000)}Cr`;
  } else if (points >= 100000) {
    return `${formatNumber(points / 100000)}L`;
  } else if (points >= 1000) {
    return `${formatNumber(points / 1000)}K`;
  }
  return points?.toString();
};

const TransactionRewards = () => {
  const [loader, setLoader] = useState(true);
  const [transactionList, setTransactionDetail] = useState([]);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const dispatch = useDispatch();
  const activePointsTab = useSelector((state) => state.transactions.activePointsTab);
  const activeTab = useSelector((state) => state.transactions.activeTab);
  const token = JSON.parse(localStorage.getItem("token_gen"));
  useEffect(() => {
    setLoader(true);
    const customerCode = localStorage.getItem("customercode");
    const customerPromise = customerView(token.access_token, customerCode);
    Promise.all([customerPromise])
      .then(([profileData]) => {
        setTransactionDetail(profileData?.transactions || []);
      })
      .catch((error) => {
        console.error("Error in API calls", error);
      })
      .finally(() => {
        setLoader(false);
      });
  }, []);

  useEffect(() => {
    return () => {
      dispatch(setActivePointTab(0));
      dispatch(setActiveTab(0));
    };
  }, [dispatch]);

  useEffect(() => {
    dispatch(setHeading("Loyalty Rewards"));
  }, [dispatch]);

  const getFilteredItems = (items, activeTab, activePointsTab) => {
    const categoryMap = {
      0: "all",
      1: "invoice",
      2: "referral",
      // 3: "erp sync",
      4: "unlocked",
      5: "used",
      6: "locked",
      7: "rejected",
    };

    const selectedCategory = categoryMap[activeTab];

    let filteredItems = items;

    if (activePointsTab === 0) {
      filteredItems = items.filter(
        (item) => item.transactionDetail?.transactionType?.toLowerCase() === "purchase"
      );
    } else if (activePointsTab === 1) {
      filteredItems = items.filter(
        (item) => item.transactionDetail?.transactionType?.toLowerCase() === "redeem"
      );
    }

    if (selectedCategory === "all" || activePointsTab === 1) return filteredItems;

    if (selectedCategory === "unlocked") {
      return filteredItems.filter(
        (item) => item.qualifiedCampaign?.length > 0
      );
    } else if (selectedCategory === "locked") {
      return filteredItems.filter(
        (item) => item.transactionDetail?.islocked?.toLowerCase() === "locked"
      );
    } else if (selectedCategory === "rejected") {
      return filteredItems.filter(
        (item) => item.transactionDetail?.is_onboarded?.toLowerCase() === "Rejected"
      );
    }

    return filteredItems.filter(
      (item) => item.transactionDetail?.activitytype?.toLowerCase() === selectedCategory
    );
  };

  const getDisplayName = (activityType, referredType) => {
    if (!activityType) return "";
    const type = activityType.toLowerCase();
    // if (type === "erp sync") return "Daily Bonus - ERP Sync";
    if (type === "invoice") return "Invoice Upload";
    if (type === "referral") {
      return referredType === "Retailer" ? "Retailer Referral" : "Distributed Referral";
    }
    return activityType;
  };

  const getTransactionImage = (activityType, referredType) => {
    if (!activityType) return "/images/transaction/dist_referral.png";
    const type = activityType.toLowerCase();

    if (type === "referral") {
      return referredType === "Retailer"
        ? "/images/transaction/retail_referral.png"
        : "/images/transaction/dist_referral.png";
    } else if (type === "invoice") {
      return "/images/transaction/invoice.png";
    }
    // else if (type === "erp sync") {
    //   return "/images/transaction/retail_referral.png";
    // }

    return "/images/transaction/dist_referral.png";
  };

  const filterButtons = () => {
    if (activePointsTab === 1) return null;

    return (
      <ul id="filter-buttons">
        <li
          className={activeTab === 0 ? "active" : ""}
          data-filter="all"
          onClick={() => dispatch(setActiveTab(0))}
        >
          All
        </li>
        <li
          className={activeTab === 1 ? "active" : ""}
          data-filter="invoice"
          onClick={() => dispatch(setActiveTab(1))}
        >
          Invoice Upload
        </li>
        <li
          className={activeTab === 2 ? "active" : ""}
          data-filter="referral"
          onClick={() => dispatch(setActiveTab(2))}
        >
          Referral
        </li>
        {/* <li
          className={activeTab === 3 ? "active" : ""}
          data-filter="erp sync"
          onClick={() => dispatch(setActiveTab(3))}
        >
          Daily Bonus - ERP Sync
        </li> */}
        <li
          className={activeTab === 4 ? "active" : ""}
          data-filter="unlocked"
          onClick={() => dispatch(setActiveTab(4))}
        >
          Unlocked
        </li>
        <li
          className={activeTab === 6 ? "active" : ""}
          data-filter="locked"
          onClick={() => dispatch(setActiveTab(6))}
        >
          Locked
        </li>
        <li
          className={activeTab === 7 ? "active" : ""}
          data-filter="rejected"
          onClick={() => dispatch(setActiveTab(7))}
        >
          Rejected
        </li>
      </ul>
    );
  };

  const handleTransactionClick = (transaction) => {
    setSelectedTransaction(transaction);
    setIsPopupOpen(true);
  };

  const closePopup = () => {
    setIsPopupOpen(false);
    setSelectedTransaction(null);
  };

  const sortedItems = useMemo(() => {
    const filteredItems = getFilteredItems(
      transactionList,
      activeTab,
      activePointsTab
    );
    return [...filteredItems].sort((a, b) => {
      const dateA =
        a.transactionDetail?.activitytype === "Invoice"
          ? parseApiDateToTimestamp(a.transactionDetail?.invoiceDate ||a.transactionDetail?.invoicedate)
          : parseApiDateToTimestamp(a.transactionProcessedDate);

      const dateB =
        b.transactionDetail?.activitytype === "Invoice"
          ? parseApiDateToTimestamp(b.transactionDetail?.invoiceDate ||a.transactionDetail?.invoicedate)
          : parseApiDateToTimestamp(b.transactionProcessedDate);

      return dateB - dateA; 
    });
  }, [transactionList, activeTab, activePointsTab]);


  const renderTransactionList = () => {
    if (!sortedItems?.length) {
      return (
        <div className="no-data">
          <p>No data found</p>
        </div>
      );
    }

    return sortedItems.map((item, index) => (
      <div
        key={index}
        className="transaction-results-box"
        data-category={item.transactionDetail?.activitytype?.toLowerCase() || ""}
        onClick={() => handleTransactionClick(item)}
        style={{ cursor: "pointer" }}
      >
        <div className="transaction-results-box-tag">
          <span>
            <img
              src={getTransactionImage(
                item.transactionDetail?.activitytype,
                item.transactionDetail?.referred_type
              )}
              alt="transaction-icon"
            />
          </span>
        </div>
        <div className="transaction-results-box-content">
          <div className="transaction-results-box-content-left">
            <h5>
              {activePointsTab === 1 && item.transactionDetail?.ItemDetails?.length > 0
                ? item.transactionDetail.ItemDetails[0].ItemName
                : getDisplayName(
                  item.transactionDetail?.activitytype,
                  item.transactionDetail?.referred_type
                )}
            </h5>

            <span>
              {activePointsTab === 1
                ? formatApiDate(item.transactionDetail?.transactionDate)
                : item.transactionDetail?.activitytype === "Invoice"
                  ? formatApiDate(item.transactionDetail?.invoiceDate || item.transactionDetail?.invoicedate || '-')
                  : getDate(item.transactionProcessedDate,"DD-MMM-YYYY")
              }
            </span>

          </div>
          <div className="transaction-results-box-content-right">
            <p className={activePointsTab === 0 ? "added" : "unlocked"}>
              {activePointsTab === 0 && item.qualifyCampaign?.length > 0 || item.transactionDetail?.points_Earned ? "+ " : activePointsTab === 1 ? "-" : ""}
              {formatPoints(
                activePointsTab === 1
                  ? item.transactionDetail?.values
                  : item.qualifiedCampaign?.length > 0
                    ? item.qualifiedCampaign[0]?.value
                    : item.transactionDetail?.points_Earned
              )}{" "}
              points
            </p>
          </div>
        </div>
      </div>
    ));
  };
  const navigate = useNavigate()
  const goToPreviousTab = () => {
    navigate(-1);
  };
  const renderPopup = () => {
    if (!isPopupOpen || !selectedTransaction) return null;

    const { transactionDetail, qualifiedCampaign } = selectedTransaction;
    const isRedeem = activePointsTab === 1;
    const points = isRedeem
      ? transactionDetail?.values
      : qualifiedCampaign?.length > 0
        ? qualifiedCampaign[0]?.value
        : transactionDetail?.points_Earned;
    const date =
      isRedeem
        ? selectedTransaction?.transactionDate
        :
        selectedTransaction?.transactionDetail?.activitytype === "Invoice" ? formatApiDate(selectedTransaction?.transactionDetail?.invoicedate ||selectedTransaction?.transactionDetail?.invoiceDate) : getDate(selectedTransaction?.transactionProcessedDate,"DD-MMM-YYYY")

    const reason = transactionDetail?.locked_reason || ""
    const invoiceid = transactionDetail?.invoiceid || ""
    const invoice = transactionDetail?.invoice_disbursal_value || ""
    const status = transactionDetail?.status || (isRedeem ? "used" : transactionDetail?.islocked ? transactionDetail?.islocked : "unlocked");

    return (
      <div className="popup" id="transactions_box" style={{ display: "block" }}>
        <div className="popup-transactionBox">
          <div className="popup-transactionBox-inner">
            <div className="popup-transactionBox-inner-header">
              <h4>Transaction Details</h4>
              <a href="javascript:void(0)" className="close-popup" onClick={closePopup}>
                X
              </a>
            </div>
            <div className="popup-transactionBox-inner-table">

              <div className="popup-transactionBox-inner-table-tr">
                <div className="popup-transactionBox-inner-table-td">
                  {isRedeem ? "Points Used" : "Points Earned"}
                </div>
                <div className="popup-transactionBox-inner-table-td color-primary">
                  {formatPoints(points)} Points
                </div>
              </div>
              {selectedTransaction?.transactionDetail?.activitytype === "Invoice" ?
                <>
                  <div className="popup-transactionBox-inner-table-tr">
                    <div className="popup-transactionBox-inner-table-td">
                      {"Invoice Disbursal Value"}
                    </div>
                    <div className="popup-transactionBox-inner-table-td color-primary">
                      ₹{formatPoints(invoice)}
                    </div>
                  </div>
                  <div className="popup-transactionBox-inner-table-tr">
                    <div className="popup-transactionBox-inner-table-td">
                      {"Invoice Id"}
                    </div>
                    <div className="popup-transactionBox-inner-table-td color-primary">
                      {invoiceid}
                    </div>
                  </div>
                </>
                : null}
              <div className="popup-transactionBox-inner-table-tr">
                <div className="popup-transactionBox-inner-table-td">Invoice Disbursal Date</div>
                <div className="popup-transactionBox-inner-table-td calendar">
                  <img src="/images/calendar.png" alt="calendar" />
                  {date || "N/A"}
                </div>
              </div>
              <div className="popup-transactionBox-inner-table-tr">
                <div className="popup-transactionBox-inner-table-td">Status</div>
                <div className="popup-transactionBox-inner-table-td">
                  <span className={status?.toLowerCase()}>
                    {status?.toUpperCase()}
                  </span>
                </div>
              </div>
            </div>
            {points === 0 ? null : (
              <div className="popup-transactionBox-inner-bottom">
                <div className="popup-transactionBox-inner-bottom-box">
                  <p>Points Status</p>
                  <p>
                    {status.toLowerCase() === "unlocked"
                      ? "These points are available for redemption"
                      : status.toLowerCase() === "locked"
                        ? reason.length > 0 ? reason : "These points are locked and not yet available"
                        : "These points have been used"}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Header goToPreviousTab={goToPreviousTab} isHomePage={false} />
      <main>
        {loader && <Loader />}
        {renderPopup()}
        <section>
          <div className="transaction">
            <div className="transaction-header">
              <ul>
                <li
                  className={activePointsTab === 0 ? "active" : ""}
                  data-item="#pe"
                  data-title="Points Earned"
                  onClick={() => {
                    dispatch(setActivePointTab(0));
                    dispatch(setActiveTab(0));
                  }}
                >
                  <p>Points Earned</p>
                </li>
                <li
                  className={activePointsTab === 1 ? "active" : ""}
                  data-item="#pu"
                  data-title="Points Used"
                  onClick={() => {
                    dispatch(setActivePointTab(1));
                    dispatch(setActiveTab(0));
                  }}
                >
                  <p>Points Used</p>
                </li>
              </ul>
            </div>
            <div className="transaction-body">
              <div className="transaction-body-content">
                <div
                  className={`transaction-body-content-inner ${activePointsTab === 0 ? "active" : ""}`}
                  id="pe"
                >
                  <div className="transaction-body-content-inner-box">
                    {filterButtons()}
                    <div className="transaction-body-content-inner-box-results">
                      {renderTransactionList(transactionList, activeTab, activePointsTab)}
                    </div>
                  </div>
                </div>
                <div
                  className={`transaction-body-content-inner ${activePointsTab === 1 ? "active" : ""}`}
                  id="pu"
                >
                  <div className="transaction-body-content-inner-box">
                    {filterButtons()}
                    <div className="transaction-body-content-inner-box-results">
                      {renderTransactionList(transactionList, activeTab, activePointsTab)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default TransactionRewards;
